import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useT } from "../../contexts/LanguageContext";

/**
 * PlansManager — admin editor for the plan catalog.
 * Lets admins tweak price, days, name/label, features, order, featured, active.
 */
export default function PlansManager() {
  const { lang } = useT();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [edits, setEdits] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
      // reset edits
      const initial = {};
      (data.plans || []).forEach((p) => { initial[p.plan_id] = { ...p }; });
      setEdits(initial);
    } catch {
      toast.error(lang === "tr" ? "Planlar yüklenemedi" : "Failed to load plans");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (plan_id, field, value) => {
    setEdits((prev) => ({ ...prev, [plan_id]: { ...prev[plan_id], [field]: value } }));
  };

  const updateFeatureLine = (plan_id, key, idx, value) => {
    const arr = [...(edits[plan_id][key] || [])];
    arr[idx] = value;
    update(plan_id, key, arr);
  };
  const addFeatureLine = (plan_id, key) => {
    update(plan_id, key, [...(edits[plan_id][key] || []), ""]);
  };
  const removeFeatureLine = (plan_id, key, idx) => {
    const arr = [...(edits[plan_id][key] || [])];
    arr.splice(idx, 1);
    update(plan_id, key, arr);
  };

  const save = async (plan_id) => {
    setSavingId(plan_id);
    try {
      const payload = { ...edits[plan_id] };
      delete payload.plan_id;
      delete payload._id;
      await api.patch(`/admin/plans/${plan_id}`, payload);
      toast.success(lang === "tr" ? "Plan güncellendi ✓" : "Plan updated ✓");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "tr" ? "Kaydedilemedi" : "Save failed"));
    } finally {
      setSavingId(null);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm(lang === "tr" ? "Tüm planları varsayılana sıfırla? Bu geri alınamaz." : "Reset all plans to defaults? Cannot be undone.")) return;
    try {
      await api.post("/admin/plans/reset");
      toast.success(lang === "tr" ? "Planlar sıfırlandı" : "Plans reset");
      await load();
    } catch {
      toast.error(lang === "tr" ? "Sıfırlanamadı" : "Reset failed");
    }
  };

  if (loading) return <div className="text-slate-500 text-sm">{lang === "tr" ? "Yükleniyor..." : "Loading..."}</div>;

  return (
    <div className="space-y-6" data-testid="plans-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-xl font-bold">{lang === "tr" ? "Fiyat Yönetimi" : "Pricing"}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {lang === "tr"
              ? "Değişikliklerin anında siteye ve ödeme akışına yansır."
              : "Changes reflect on site and checkout immediately."}
          </p>
        </div>
        <button onClick={resetToDefaults} className="btn-secondary text-xs" data-testid="plans-reset-defaults">
          {lang === "tr" ? "Varsayılana sıfırla" : "Reset to defaults"}
        </button>
      </div>

      {plans.map((p) => {
        const e = edits[p.plan_id] || {};
        return (
          <div key={p.plan_id} className="card" data-testid={`plan-editor-${p.plan_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="overline text-[#002FA7]">{p.plan_id.toUpperCase()}</span>
                {e.featured && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">ÖNERİLEN</span>}
                {!e.active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">PASİF</span>}
                {e.hidden_from_checkout && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">CHECKOUT'TA YOK</span>}
              </div>
              <button
                onClick={() => save(p.plan_id)}
                disabled={savingId === p.plan_id}
                className="btn-primary text-xs"
                data-testid={`plan-save-${p.plan_id}`}
              >
                {savingId === p.plan_id ? "..." : (lang === "tr" ? "Kaydet" : "Save")}
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm">
                <span className="text-xs font-semibold text-slate-600 uppercase">Ad (TR)</span>
                <input type="text" value={e.name_tr || ""} onChange={(ev) => update(p.plan_id, "name_tr", ev.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" />
              </label>
              <label className="text-sm">
                <span className="text-xs font-semibold text-slate-600 uppercase">Name (EN)</span>
                <input type="text" value={e.name_en || ""} onChange={(ev) => update(p.plan_id, "name_en", ev.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" />
              </label>
              <label className="text-sm">
                <span className="text-xs font-semibold text-slate-600 uppercase">Fiyat (₺, KDV dahil)</span>
                <input type="number" min="0" step="1" value={e.price_total ?? 0}
                  onChange={(ev) => update(p.plan_id, "price_total", parseFloat(ev.target.value || "0"))}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" data-testid={`plan-price-${p.plan_id}`} />
              </label>
              <label className="text-sm">
                <span className="text-xs font-semibold text-slate-600 uppercase">Gün Sayısı</span>
                <input type="number" min="1" max="1825" value={e.days ?? 30}
                  onChange={(ev) => update(p.plan_id, "days", parseInt(ev.target.value || "1"))}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" data-testid={`plan-days-${p.plan_id}`} />
              </label>
              <label className="text-sm">
                <span className="text-xs font-semibold text-slate-600 uppercase">Sıra (0 en üst)</span>
                <input type="number" value={e.order ?? 0} onChange={(ev) => update(p.plan_id, "order", parseInt(ev.target.value || "0"))}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" />
              </label>
              <div className="flex items-end gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!e.featured} onChange={(ev) => update(p.plan_id, "featured", ev.target.checked)} />
                  <span>{lang === "tr" ? "Öne çıkar (ÖNERİLEN rozeti)" : "Featured"}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!e.active} onChange={(ev) => update(p.plan_id, "active", ev.target.checked)} />
                  <span>{lang === "tr" ? "Aktif" : "Active"}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!e.hidden_from_checkout} onChange={(ev) => update(p.plan_id, "hidden_from_checkout", ev.target.checked)} />
                  <span>{lang === "tr" ? "Checkout'ta gizle" : "Hide from checkout"}</span>
                </label>
              </div>
            </div>

            {/* Features TR */}
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-600 uppercase mb-1">Özellikler (TR)</div>
              {(e.features_tr || []).map((f, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <input value={f} onChange={(ev) => updateFeatureLine(p.plan_id, "features_tr", i, ev.target.value)}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" />
                  <button onClick={() => removeFeatureLine(p.plan_id, "features_tr", i)} className="text-red-500 text-xs px-2">✕</button>
                </div>
              ))}
              <button onClick={() => addFeatureLine(p.plan_id, "features_tr")} className="text-xs text-[#002FA7] font-semibold">+ Özellik ekle (TR)</button>
            </div>
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-600 uppercase mb-1">Features (EN)</div>
              {(e.features_en || []).map((f, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <input value={f} onChange={(ev) => updateFeatureLine(p.plan_id, "features_en", i, ev.target.value)}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" />
                  <button onClick={() => removeFeatureLine(p.plan_id, "features_en", i)} className="text-red-500 text-xs px-2">✕</button>
                </div>
              ))}
              <button onClick={() => addFeatureLine(p.plan_id, "features_en")} className="text-xs text-[#002FA7] font-semibold">+ Feature (EN)</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
